import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Container,
  Stack,
  Typography,
  Button,
  useTheme,
  Collapse,
  Slide,
  Divider,
  Fade
} from '@mui/material';
import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import toast from 'react-hot-toast';
import { TransitionGroup } from 'react-transition-group';
import _ from 'lodash';
import indexFinder from '../../../utils/indexFinder';
// services
import ServicesDetailKSEQuestions from './ServicesDetailKSEQuestions';
import ServicesDetailPermohonanQuestions from './ServicesDetailPermohonanQuestions';
import ServicesDetailFeedbackQuestions from './ServicesDetailFeedbackQuestions';

ServicesDetail.propTypes = {
  layanan: PropTypes.object.isRequired,
  getLayanan: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

export default function ServicesDetail({ layanan, getLayanan, user }) {
  // {JSON.stringify(layanan.meta?.data_permohonan?.JenisPermohonan?.metadata?.requirements)}
  const requirements = layanan.meta?.data_permohonan?.JenisPermohonan?.metadata?.requirements;
  const sortedRequirements = _.orderBy(requirements, 'id', 'asc');
  const [selectedRequirement, setSelectedRequirement] = React.useState({});
  const [requirementIndex, setRequirementIndex] = React.useState();
  const [questions, setQuestions] = React.useState([]);
  const [informations, setInformations] = React.useState([]);
  const [toggleUpdate, setToggleUpdate] = React.useState(false);

  React.useEffect(() => {
    const selected = requirements?.[requirementIndex];

    setQuestions(selected?.meta?.questions ?? []); // default array
    setInformations(selected?.meta?.informations ?? []); // default array
  }, [requirementIndex, requirements]);

  React.useEffect(() => {
    const index = indexFinder(requirements, selectedRequirement.type, 'type');
    setRequirementIndex(index);
  }, [selectedRequirement]);

  React.useEffect(() => {
    getLayanan();
    setQuestions([]);
  }, [selectedRequirement]);

  React.useEffect(() => {
    getLayanan();
  }, [toggleUpdate]);

  React.useEffect(() => {
    if (!requirements || requirements.length === 0) return;

    const index =
      layanan.type === 'subdomain'
        ? indexFinder(requirements, 'kategori_se', 'type')
        : indexFinder(requirements, 'data_permohonan', 'type');

    // Kalau index tidak ditemukan (-1), jangan set
    if (index === -1) return;

    setSelectedRequirement(requirements[index]);
  }, [requirements, layanan.type]);

  return (
    <Container maxWidth="xl">
      {/* {JSON.stringify(layanan?.meta?.data_permohonan)} */}
      <Box>
        <Stack direction="row" flexWrap="wrap">
          {/* {JSON.stringify(layanan)} */}
          {sortedRequirements?.map((item) => (
            <TransitionGroup key={item.id}>
              <RequiredItems
                selectedRequirement={selectedRequirement}
                setSelectedRequirement={setSelectedRequirement}
                requirement={item}
                layanan={layanan}
                user={user}
              />
            </TransitionGroup>
          ))}
        </Stack>
      </Box>

      <Box pb={3}>
        {selectedRequirement.type === 'data_permohonan' &&
          !_.isEmpty(questions) &&
          !_.isEmpty(informations) && (
            <ServicesDetailPermohonanQuestions
              user={user}
              getLayanan={getLayanan}
              layanan={layanan}
              questions={questions}
              setQuestions={setQuestions}
              selectedRequirement={selectedRequirement}
              informations={informations}
              toggleUpdate={toggleUpdate}
              setToggleUpdate={setToggleUpdate}
            />
          )}
        {selectedRequirement.type === 'kategori_se' &&
          !_.isEmpty(questions) &&
          !_.isEmpty(informations) && (
            <ServicesDetailKSEQuestions
              user={user}
              layanan={layanan}
              questions={questions}
              setQuestions={setQuestions}
              selectedRequirement={selectedRequirement}
              informations={informations}
              toggleUpdate={toggleUpdate}
              setToggleUpdate={setToggleUpdate}
            />
          )}

        {selectedRequirement.type === 'feedback' &&
          !_.isEmpty(questions) &&
          !_.isEmpty(informations) && (
            <ServicesDetailFeedbackQuestions
              user={user}
              layanan={layanan}
              questions={questions}
              setQuestions={setQuestions}
              selectedRequirement={selectedRequirement}
              informations={informations}
              toggleUpdate={toggleUpdate}
              setToggleUpdate={setToggleUpdate}
            />
          )}
      </Box>
    </Container>
  );
}

// RequiredItems

RequiredItems.propTypes = {
  selectedRequirement: PropTypes.object.isRequired,
  setSelectedRequirement: PropTypes.func.isRequired,
  requirement: PropTypes.object.isRequired,
  layanan: PropTypes.object.isRequired
};

function RequiredItems({
  selectedRequirement,
  setSelectedRequirement,
  requirement,
  layanan,
  user
}) {
  let signed = false;
  if (requirement.type === 'data_permohonan') {
    if (layanan?.meta?.data_permohonan?.is_signed && layanan?.meta?.data_permohonan?.document_path)
      signed = true;
  }
  if (requirement.type === 'kategori_se') {
    if (layanan?.meta?.kategori_se?.is_signed && layanan?.meta?.kategori_se?.document_path)
      signed = true;
  }

  if (requirement.type === 'feedback') {
    if (layanan?.meta?.feedback?.is_signed && layanan?.meta?.feedback?.document_path) signed = true;

    if (
      layanan?.meta?.data_permohonan?.JenisPermohonan?.metadata?.requirements[
        indexFinder(
          layanan?.meta?.data_permohonan?.JenisPermohonan?.metadata?.requirements,
          'feedback',
          'type'
        )
      ]?.attributes?.question_id !== 'rekomendasi'
    ) {
      if (layanan?.meta?.feedback?.status) signed = true;
    }
  }

  const BoxStyle = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    boxShadow: 'none',
    textAlign: 'center',
    padding: theme.spacing(3),
    margin: theme.spacing(1),
    color: signed ? theme.palette.grey.darker : theme.palette.warning.darker,
    backgroundColor:
      // (requirement.attributes.question_id === 'tte' &&
      //   !_.isEmpty(layanan?.meta?.data_permohonan?.metadata?.status === 'saved')) ||
      signed ? theme.palette.grey[200] : theme.palette.warning.lighter
  }));

  const theme = useTheme();

  const getBoxText = () => {
    let text = '';
    if (['admin', 'superadmin'].includes(user?.attributes?.role)) {
      text = 'Klik di sini untuk melihat data';
      if (requirement.type === 'feedback' && !signed) text = 'Klik di sini untuk melengkapi data';
    } else {
      if (!signed) text = 'Klik di sini untuk melihat data';
      if (signed) text = 'Klik di sini untuk melengkapi data';
    }

    return text;
  };
  return (
    <BoxStyle
      onClick={() => {
        if (selectedRequirement.id === requirement.id) return;
        if (requirement.type === 'kategori_se') {
          if (!layanan?.meta?.data_permohonan?.metadata?.answers?.nip_kepala_dinas) {
            if (['admin', 'superadmin'].includes(user?.attributes?.role))
              return toast.error('Belum ada data kategorisasi sistem elektronik');
          }
        }
        if (requirement.type === 'data_permohonan' && layanan.type === 'subdomain') {
          if (!layanan?.meta?.kategori_se?.is_signed) {
            return toast.error(
              'Harap lengkapi dan TTE formulir Kategorisasi Elektronik terlebih dahulu'
            );
          }
        }
        if (requirement.type === 'feedback') {
          if (['admin', 'superadmin'].includes(user?.attributes?.role)) {
            if (!['tte'].includes(selectedRequirement.attributes.question_id)) {
              if (
                !layanan?.meta?.data_permohonan?.is_signed ||
                !layanan?.meta?.data_permohonan?.document_path
              ) {
                if (layanan?.meta?.data_permohonan?.metadata?.tipe_permohonan !== 'archive')
                  return toast.error('Dokumen permohonan belum ditandatangani');
              }

              // if (layanan?.attributes?.status !== 'approved')
              //   return toast.error('Dokumen permohonan belum disetujui');
            }
          }
        }

        setSelectedRequirement(requirement);
      }}
      sx={
        selectedRequirement.id === requirement.id
          ? {
              border: 2,
              boxShadow: 5,
              borderColor: signed ? theme.palette.primary.main : theme.palette.warning.main
            }
          : {}
      }
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ minWidth: 200, maxWidth: 200, maxHeight: 180, minHeight: 180 }}
      >
        <Box pb={2}>
          <Typography variant="h5">{requirement.attributes.label}</Typography>
        </Box>
        <Box>
          {!_.isEmpty(layanan?.meta?.data_permohonan?.is_signed) ? (
            <Icon
              width={48}
              height={48}
              icon={checkmarkCircleFill}
              color={theme.palette.primary.main}
            />
          ) : (
            <Icon
              width={48}
              height={48}
              icon={signed ? checkmarkCircleFill : closeCircleFill}
              color={signed ? theme.palette.primary.main : theme.palette.error.light}
            />
          )}
        </Box>
        <Box color="red">
          <Typography
            variant="body2"
            sx={{ fontStyle: 'italic', color: theme.palette.text.primary }}
          >
            {getBoxText()}
          </Typography>
        </Box>
      </Stack>
    </BoxStyle>
  );
}
